import { useReducedMotion } from "framer-motion";
import { OttoAnimation } from "@/features/otto/brand/OttoAnimation";

/**
 * As artes animadas que um destaque pode pedir pela chave (`artKey`). A lista de chaves válidas é a do domínio
 * (`SPOTLIGHT_ART_KEYS`); `SpotlightArt.test.jsx` confere que todas têm desenho aqui, para o admin nunca escolher uma
 * arte que a tela não sabe desenhar.
 *
 * Cada arte recebe `dark` (fundo escuro) e devolve o desenho já na cor certa.
 */
export const SPOTLIGHT_ARTS = Object.freeze({
  otto: {
    label: "Otto",
    render: ({ dark }) => (
      <OttoAnimation
        ink={dark ? "#FFFFFF" : "#0F1115"}
        label="Otto: o logotipo se transforma no ícone da chamada de vídeo"
        className="h-full w-full"
      />
    ),
  },
});

export const SPOTLIGHT_ART_KEYS = Object.freeze(Object.keys(SPOTLIGHT_ARTS));

/** Rótulo da arte para o formulário do admin. */
export const artLabel = (key) => SPOTLIGHT_ARTS[key]?.label ?? key;

/**
 * O que ocupa o lado direito do destaque: a arte animada (se o cadastro pediu uma que existe), senão a mídia enviada
 * (imagem, gif ou vídeo), senão um desenho abstrato neutro. Sempre decorativo para quem usa leitor de tela, exceto a arte
 * animada, que tem nome.
 *
 * @param {{ artKey?: string|null, mediaUrl?: string|null, mediaType?: string|null, dark?: boolean, title?: string }} props
 */
export default function SpotlightArt({ artKey = null, mediaUrl = null, mediaType = null, dark = true, title = "" }) {
  const reduced = useReducedMotion();
  const art = artKey ? SPOTLIGHT_ARTS[artKey] : null;

  if (art) return <div data-spotlight-art={artKey} className="h-full w-full">{art.render({ dark })}</div>;

  if (mediaUrl && mediaType === "video") {
    return (
      <video
        src={mediaUrl}
        data-spotlight-art="video"
        aria-label={title || undefined}
        aria-hidden={title ? undefined : "true"}
        muted
        loop
        playsInline
        // Quem pediu menos movimento vê o primeiro quadro parado, sem tocar sozinho
        autoPlay={!reduced}
        className="h-full w-full rounded-[13px] object-cover"
      />
    );
  }
  if (mediaUrl) {
    return <img src={mediaUrl} data-spotlight-art="image" alt="" aria-hidden="true" className="h-full w-full rounded-[13px] object-cover" />;
  }
  return (
    <div
      data-spotlight-art="abstract"
      aria-hidden="true"
      className="h-full w-full rounded-[13px] border border-black/[.06] bg-[linear-gradient(150deg,rgba(255,255,255,.9),rgba(255,255,255,.55))] p-4 dark:border-white/[.13] dark:bg-[linear-gradient(150deg,rgba(255,255,255,.17),rgba(255,255,255,.045))]"
    >
      <span className="block h-1.5 w-2/5 rounded-sm bg-black/20 dark:bg-white/30" />
      <span className="mt-3 block h-1.5 w-[84%] rounded-sm bg-black/[.10] dark:bg-white/[.15]" />
      <span className="mt-1.5 block h-1.5 w-[66%] rounded-sm bg-black/[.10] dark:bg-white/[.15]" />
    </div>
  );
}
